import { ArrowRight, MagnifyingGlass, Receipt, WarningCircle } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { OrderStatusBadge, canReviewOrder, isOrderActive, orderProductPath, useGetOrdersQuery } from '@entities/order';
import { ProductLogo } from '@entities/product';
import { AnimatedTabs } from '@shared/ui/AnimatedTabs';
import { Button, ButtonLink } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { EmptyState } from '@shared/ui/EmptyState';
import { Screen } from '@shared/ui/Screen';
import { SearchField } from '@shared/ui/SearchField';
import { SectionSkeleton } from '@widgets/PageSkeleton/PageSkeleton';
import styles from './OrdersPage.module.css';

type OrderFilter = 'all' | 'processing' | 'delivered' | 'issues';
const FILTERS = [
  { id: 'all', label: 'All orders' },
  { id: 'processing', label: 'Processing' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'issues', label: 'Refunded & failed' },
] as const;

export function OrdersPage() {
  const [filter, setFilter] = useState<OrderFilter>('all');
  const [query, setQuery] = useState('');
  const [polling, setPolling] = useState(false);
  const { data: orders, isLoading, isError, isFetching, refetch } = useGetOrdersQuery(undefined, {
    pollingInterval: polling ? 5_000 : 0,
    refetchOnMountOrArgChange: true,
  });

  // Stop background requests once every loaded order is settled.
  useEffect(() => setPolling(orders?.some(isOrderActive) ?? false), [orders]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (orders ?? []).filter((order) => {
      const matchesStatus = filter === 'all'
        || (filter === 'processing' && isOrderActive(order))
        || (filter === 'delivered' && order.status === 'COMPLETED')
        || (filter === 'issues' && ['REFUNDED', 'FAILED'].includes(order.status));
      return matchesStatus && (!needle || [order.productName, order.id, order.shopName ?? ''].some(
        (text) => text.toLowerCase().includes(needle),
      ));
    });
  }, [orders, filter, query]);

  return (
    <Screen title="My orders">
      <div className={styles.page}>
        <header className={styles.heading}>
          <div><h1>My orders</h1><p>Your digital finds, all in one place.</p></div>
          <ButtonLink to="/" variant="secondary" className={styles.shopButton}>Keep shopping <ArrowRight size={16} /></ButtonLink>
        </header>

        <SearchField query={query} onChange={setQuery} placeholder="Search your purchases or order ID" flush />
        <AnimatedTabs items={FILTERS} value={filter} onChange={setFilter} ariaLabel="Filter orders by status" />

        {isLoading && !orders ? <SectionSkeleton variant="orders" /> : null}
        {isError ? (
          <div className={styles.empty} role="alert">
            <EmptyState icon={WarningCircle} title="Couldn't refresh your orders" description="Please try again to see the latest order status." />
            <Button variant="secondary" loading={isFetching} onClick={() => void refetch()}>Try again</Button>
          </div>
        ) : null}

        {!isLoading && !isError && orders?.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState icon={Receipt} title="Your next great find starts here" description="After you buy, track your order and find your digital items here." />
            <ButtonLink to="/">Explore products</ButtonLink>
          </div>
        ) : null}

        {!isLoading && !isError && Boolean(orders?.length) && visible.length === 0 ? (
          <div className={styles.empty}>
            <EmptyState icon={MagnifyingGlass} title="No matching orders" description="Try another search or view all of your recent orders." />
            <Button variant="secondary" onClick={() => { setQuery(''); setFilter('all'); }}>Clear filters</Button>
          </div>
        ) : null}

        {orders && orders.length > 0 ? (
          <>
            <p className={styles.results} aria-live="polite">
              {visible.length} {visible.length === 1 ? 'order' : 'orders'}{orders.length === 50 ? ' · Showing your latest 50 orders' : ''}
            </p>
            <div className={styles.list}>
              {visible.map((order) => (
                <Card key={order.id} className={styles.order}>
                  <div className={styles.orderTop}>
                    <OrderStatusBadge status={order.status} />
                    <time dateTime={order.createdAt}>{new Date(order.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</time>
                  </div>
                  <Link to={`/orders/${encodeURIComponent(order.id)}`} className={styles.product} aria-label={`View order for ${order.productName}`}>
                    <ProductLogo src={order.productLogoUrl || `/logos/${encodeURIComponent(order.productId)}.webp`} className={styles.thumbnail} width={76} height={76} loading="lazy" />
                    <div className={styles.productInfo}>
                      <h2>{order.productName}</h2>
                      <p>{order.shopName || 'Digital purchase'} · Qty {order.quantity}</p>
                      {order.kind === 'GIVEAWAY' ? <span className={styles.giveaway}>Giveaway prize</span> : null}
                    </div>
                    <span className={styles.price}>{order.kind === 'GIVEAWAY' ? 'FREE' : order.pricePaid.label}</span>
                  </Link>
                  <div className={styles.orderBottom}>
                    <span className={styles.orderId}>Order #{order.id.slice(0, 8).toUpperCase()}</span>
                    <div className={styles.actions}>
                      {canReviewOrder(order) ? <ButtonLink variant="ghost" to={`${orderProductPath(order)}?review=1`}>Review</ButtonLink> : null}
                      <ButtonLink variant="secondary" to={`/orders/${encodeURIComponent(order.id)}`}>
                        {order.status === 'COMPLETED' ? 'View my items' : 'View order'} <ArrowRight size={14} />
                      </ButtonLink>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </Screen>
  );
}
